import type { OnboardingStatus } from "@xop-any-time/shared";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useState, type FormEvent, type JSX } from "react";

import { ApiError } from "../../lib/api.js";
import { checkPdf, submitOnboardingDocuments, templateUrl } from "../../lib/onboardingApi.js";
import { AuthNotice, SubmitButton } from "../login/AuthCard.js";
import { WallCard } from "./OnboardingLayout.js";

/**
 * Step 1 (spec E §10.2): download the two templates, sign them however you
 * sign things, upload the signed PDFs. The system does not verify a
 * signature — a human reviews the file; what is checked here is only that
 * each file is a PDF under 10 MB, mirroring the server.
 */
export function SignDocumentsStep({
  onSubmitted,
}: {
  onSubmitted: (next: OnboardingStatus) => void;
}): JSX.Element {
  const [agreement, setAgreement] = useState<File | null>(null);
  const [nda, setNda] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const problem =
      (checkPdf(agreement) && `Reseller Agreement: ${checkPdf(agreement)}`) ||
      (checkPdf(nda) && `NDA: ${checkPdf(nda)}`);
    if (problem) {
      setError(problem);
      return;
    }
    setSubmitting(true);
    try {
      onSubmitted(await submitOnboardingDocuments(agreement!, nda!));
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "We couldn't upload your documents just now. Please try again in a moment.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WallCard title="Sign the documents">
      <Typography component="p" sx={{ mt: 0.75, fontSize: 13.5, lineHeight: 1.6, color: "text.secondary" }}>
        Download both documents, sign them, and upload the signed copies as PDFs. XOP Networks reviews
        them and countersigns; your account is activated when that is done.
      </Typography>

      <Box sx={{ mt: 3, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
        <DownloadCard
          title="Reseller Agreement"
          href={templateUrl("reseller-agreement")}
          filename="XOP-Reseller-Agreement.pdf"
          label="Download the Reseller Agreement"
        />
        <DownloadCard
          title="Non-Disclosure Agreement"
          href={templateUrl("reseller-nda")}
          filename="XOP-Reseller-NDA.pdf"
          label="Download the NDA"
        />
      </Box>

      <Box component="form" onSubmit={onSubmit} noValidate sx={{ mt: 3.5, display: "grid", gap: 2 }}>
        {error && <AuthNotice>{error}</AuthNotice>}
        <FileField id="signed_agreement" label="Signed Reseller Agreement (PDF)" file={agreement} onChange={setAgreement} disabled={submitting} />
        <FileField id="signed_nda" label="Signed NDA (PDF)" file={nda} onChange={setNda} disabled={submitting} />
        <SubmitButton disabled={submitting}>
          {submitting ? "Uploading…" : "Submit signed documents"}
        </SubmitButton>
      </Box>
    </WallCard>
  );
}

function DownloadCard({ title, href, filename, label }: { title: string; href: string; filename: string; label: string }): JSX.Element {
  return (
    <Box
      sx={{
        borderRadius: 1.5,
        border: 1,
        borderColor: "divider",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Typography component="p" sx={{ fontWeight: 600, fontSize: 14 }}>
        {title}
      </Typography>
      <Box
        component="a"
        href={href}
        download={filename}
        aria-label={label}
        sx={{
          fontSize: 13,
          fontWeight: 500,
          color: (t) => (t.palette.mode === "dark" ? t.palette.accent[400] : t.palette.accent[600]),
          textDecoration: "none",
          "&:hover": { textDecoration: "underline", textUnderlineOffset: 3 },
        }}
      >
        Download PDF
      </Box>
    </Box>
  );
}

/** A labelled file input in the sign-in card's frame. The native control is
 * kept (not hidden behind a styled button): it is the one thing on the page
 * every browser, screen reader and password manager already understands. */
function FileField({
  id,
  label,
  file,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  file: File | null;
  onChange: (f: File | null) => void;
  disabled: boolean;
}): JSX.Element {
  return (
    <Box>
      <Typography component="label" htmlFor={id} sx={{ display: "block", mb: 0.75, fontSize: 12.5, fontWeight: 500 }}>
        {label}
      </Typography>
      <Box
        component="input"
        id={id}
        type="file"
        accept="application/pdf"
        disabled={disabled}
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
        sx={{
          width: "100%",
          borderRadius: 1.5,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.default",
          px: 1.5,
          py: 1.25,
          fontFamily: "inherit",
          fontSize: 13.5,
          color: "text.primary",
          "&:focus-visible": (t) => ({
            outline: "none",
            borderColor: t.palette.accent[500],
            boxShadow: `0 0 0 2px ${alpha(t.palette.accent[500], 0.2)}`,
          }),
        }}
      />
      {file && (
        <Typography component="p" sx={{ mt: 0.5, fontSize: 12, color: "text.secondary" }}>
          {file.name} · {(file.size / 1024).toFixed(0)} KB
        </Typography>
      )}
    </Box>
  );
}
